import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { DocumentTypeChipComponent } from '../document-type-chip/document-type-chip.component';
import { ResponseDocumentSubtype } from '../../../models/response-document-subtype';
import { getDocumentTypeColor } from '../document-types';

@Component({
  selector: 'app-document-type-filter-bar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule, DocumentTypeChipComponent],
  templateUrl: './document-type-filter-bar.component.html',
  styleUrls: ['./document-type-filter-bar.component.scss'],
})
export class DocumentTypeFilterBarComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() subtypes: ResponseDocumentSubtype[] = [];
  @Input() activeIds: number[] = [];
  @Input() counts: Record<number, number> = {};
  /** Selected docs per subtype — when set, chips show selected/total. */
  @Input() selectedCounts: Record<number, number> = {};
  @Input() showAllChip = true;
  @Input() allLabel = 'tenders.library.filter-all';
  /** Wrap all chips with no “+N autres” overflow measure (picker). */
  @Input() wrapAll = false;
  @Input() size: 'default' | 'compact' = 'default';

  readonly toggle = output<number>();
  readonly clear = output<void>();

  @ViewChild('row') rowRef?: ElementRef<HTMLElement>;
  @ViewChild('measure') measureRef?: ElementRef<HTMLElement>;

  expanded = false;
  visibleCount = Number.MAX_SAFE_INTEGER;
  /** How many categories were hidden last time we measured (kept while expanded). */
  overflowCount = 0;
  /** Stable slice for the template — avoid allocating a new array every CD cycle. */
  visibleSubtypes: ResponseDocumentSubtype[] = [];

  private resizeObserver?: ResizeObserver;
  private resizeRaf = 0;
  private subtypesKey = '';
  private countsKey = '';

  constructor(
    private cdr: ChangeDetectorRef,
    private zone: NgZone
  ) {}

  ngAfterViewInit(): void {
    this.syncVisibleSubtypes();
    if (this.wrapAll) return;
    this.recomputeVisible();
    if (typeof ResizeObserver === 'undefined' || !this.rowRef?.nativeElement) return;

    // Keep ResizeObserver out of Angular's zone so layout thrashing cannot schedule CD loops.
    this.zone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
        this.resizeRaf = requestAnimationFrame(() => {
          this.resizeRaf = 0;
          this.recomputeVisible();
        });
      });
      this.resizeObserver.observe(this.rowRef!.nativeElement);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['wrapAll'] && this.wrapAll) {
      this.expanded = true;
      this.visibleCount = this.subtypes.length;
      this.overflowCount = 0;
      this.syncVisibleSubtypes();
      this.resizeObserver?.disconnect();
      this.resizeObserver = undefined;
    }
    if (changes['subtypes']) {
      const key = this.subtypes.map(s => s.id).join(',');
      if (key !== this.subtypesKey) {
        this.subtypesKey = key;
        this.syncVisibleSubtypes();
        if (!this.wrapAll) queueMicrotask(() => this.recomputeVisible());
      }
    }
    if (changes['counts'] && !this.wrapAll) {
      const key = Object.entries(this.counts)
        .map(([id, n]) => `${id}:${n}`)
        .sort()
        .join(',');
      if (key !== this.countsKey) {
        this.countsKey = key;
        queueMicrotask(() => this.recomputeVisible());
      }
    }
    if (changes['selectedCounts']) {
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
  }

  get isAllActive(): boolean {
    return this.activeIds.length === 0;
  }

  get hiddenCount(): number {
    if (this.wrapAll) return 0;
    return this.expanded ? this.overflowCount : Math.max(0, this.subtypes.length - this.visibleCount);
  }

  isActive(id: number): boolean {
    return this.activeIds.includes(id);
  }

  colorFor(subtype: ResponseDocumentSubtype): string {
    return getDocumentTypeColor(subtype.name);
  }

  onAll(): void {
    this.clear.emit();
  }

  expand(): void {
    this.expanded = true;
    this.syncVisibleSubtypes();
    this.cdr.markForCheck();
  }

  collapse(): void {
    if (this.wrapAll) return;
    this.expanded = false;
    this.syncVisibleSubtypes();
    this.cdr.markForCheck();
    queueMicrotask(() => this.recomputeVisible());
  }

  private recomputeVisible(): void {
    if (this.wrapAll) {
      this.setVisible(this.subtypes.length, 0);
      return;
    }
    const row = this.rowRef?.nativeElement;
    const measure = this.measureRef?.nativeElement;
    if (!row || !measure) {
      this.setVisible(this.subtypes.length, 0);
      return;
    }
    // While expanded we keep the last overflowCount so "Réduire" stays visible.
    if (this.expanded) {
      return;
    }

    const avail = row.clientWidth;
    // Not laid out yet — wait for a real resize instead of oscillating on width 0.
    if (avail <= 0) return;

    const kids = Array.from(measure.children) as HTMLElement[];
    if (kids.length < 2) {
      this.setVisible(this.subtypes.length, 0);
      return;
    }

    const gap = 6;
    const hasAll = this.showAllChip ? 1 : 0;
    const allW = hasAll ? kids[0].offsetWidth : 0;
    const moreW = kids[kids.length - 1].offsetWidth;
    const catWs = this.subtypes.map((_, i) => kids[hasAll + i]?.offsetWidth ?? 0);

    let total = allW;
    let fitAll = true;
    for (let i = 0; i < this.subtypes.length; i++) {
      total += (total > 0 || hasAll ? gap : 0) + catWs[i];
      if (total > avail) {
        fitAll = false;
        break;
      }
    }
    if (fitAll) {
      this.setVisible(this.subtypes.length, 0);
      return;
    }

    let used = allW;
    let k = 0;
    for (let i = 0; i < this.subtypes.length; i++) {
      const next = used + (used > 0 || hasAll ? gap : 0) + catWs[i];
      if (next + gap + moreW <= avail) {
        used = next;
        k++;
      } else {
        break;
      }
    }
    const visible = Math.max(1, k);
    this.setVisible(visible, Math.max(0, this.subtypes.length - visible));
  }

  private setVisible(visibleCount: number, overflowCount: number): void {
    if (this.visibleCount === visibleCount && this.overflowCount === overflowCount) return;
    this.visibleCount = visibleCount;
    this.overflowCount = overflowCount;
    this.syncVisibleSubtypes();
    // Re-enter the zone only when the visible set actually changed.
    this.zone.run(() => this.cdr.markForCheck());
  }

  private syncVisibleSubtypes(): void {
    this.visibleSubtypes = this.wrapAll || this.expanded ? this.subtypes : this.subtypes.slice(0, this.visibleCount);
  }
}
